// Product create/edit form, shared by the Products page and the invoice editor.

import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { fieldErrors, useErrorMessage } from '../lib/hooks';
import { useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { Checkbox, ErrorAlert, TextField } from './ui';
import type { Product } from '@shared/types';

export interface ProductFormProps {
  product?: Product;
  onSaved: (product: Product, created: boolean) => void;
  onCancel: () => void;
}

export function ProductForm({ product, onSaved, onCancel }: ProductFormProps) {
  const t = useT();
  const { money } = useSession();
  const toMessage = useErrorMessage();

  const [code, setCode] = useState(product?.code ?? '');
  const [name, setName] = useState(product?.name ?? '');
  const [unitPrice, setUnitPrice] = useState(product ? String(product.unitPrice) : '');
  const [isActive, setIsActive] = useState(product?.isActive ?? true);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    setFormError(undefined);
    const payload = {
      code: code.trim(),
      name: name.trim(),
      // Accept a comma as the decimal separator: Spanish keyboards produce one.
      unitPrice: Number(unitPrice.replace(',', '.')) || 0,
      isActive,
    };
    try {
      const saved = product
        ? await api.updateProduct(product.id, payload)
        : await api.createProduct(payload);
      onSaved(saved, !product);
    } catch (err) {
      const fields = fieldErrors(err);
      setErrors(fields);
      if (Object.keys(fields).length === 0) setFormError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="stack">
      {formError && <ErrorAlert message={formError} />}

      <div className="form-grid form-grid--2">
        <TextField label={t('products.field.code')} required autoFocus
          value={code} error={errors.code} onChange={(e) => setCode(e.target.value)} />
        <TextField label={`${t('products.field.unitPrice')} (${money.symbol})`} required
          numeric inputMode="decimal"
          value={unitPrice} error={errors.unitPrice} onChange={(e) => setUnitPrice(e.target.value)} />
        <TextField label={t('products.field.name')} required wrapperClassName="span-all"
          value={name} error={errors.name} onChange={(e) => setName(e.target.value)} />
      </div>

      <Checkbox label={t('common.active')} checked={isActive} onChange={setIsActive} />

      <div className="row row--end">
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy && <span className="spinner" />}
          {busy ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  );
}
